import { SubtitlesErrorService } from '../subtitles-error/subtitles-error.service';
import { subtitlesConfig } from '../../config/subtitles.config';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, catchError, finalize, of, tap } from 'rxjs';

import { FileErrorTypes, FileValidatedInfo } from '../../models';
import { SubtitlesUploaderHttpsService } from '../subtitles-https/subtitles-https.service';
import { SubtitlesService } from '../../../../services/subtitles.service';

@Injectable()
export class SubtitlesValidateService {
    private fileUploaderHttpsService = inject(SubtitlesUploaderHttpsService);
    private fileErrorService = inject(SubtitlesErrorService);
    private subtitlesService = inject(SubtitlesService);

    private _validatingFile$ = new BehaviorSubject<File | null>(null);
    private _validating$ = new BehaviorSubject<boolean | null>(false);
    private _validateEvent$ = new Subject<File>();
    private _validatedInfo$ = new BehaviorSubject<FileValidatedInfo | null>(null);
    validatedInfo$ = this._validatedInfo$.asObservable();
    validatingFile$ = this._validatingFile$.asObservable();
    validating$ = this._validating$.asObservable();
    validateEvent$ = this._validateEvent$.asObservable();

    private readonly subscriptions$ = new Subscription();

    init(): void {
        this.subscriptions$.add(
            this.validateEvent$
                .pipe(
                    tap((file) => {
                        const formData = new FormData();
                        formData.append('file', file);
                        this.validateSubtitlesFile(formData, file);
                    }),
                )
                .subscribe(),
        );
    }

    destroy(): void {
        this.subscriptions$.unsubscribe();
    }

    resetValues(): void {
        this._validating$.next(null);
        this._validatingFile$.next(null);
        this._validatedInfo$.next(null);
    }

    validate(file: File): void {
        this._validateEvent$.next(file);
    }

    private validateSubtitlesFile(formData: FormData, file: File): void {
        this._validatingFile$.next(file);
        this.setValidating(true);

        if (this.wrongExtension(file.name)) {
            this.fileErrorService.setError('extension');
            this.setValidating(false);
            return;
        }

        this.subscriptions$.add(
            this.fileUploaderHttpsService
                .validateSubtitlesFile(formData, subtitlesConfig.validateUrl)
                .pipe(
                    tap(({ hash = '', error }) => {
                        if (error) {
                            this.fileErrorService.setError(error);
                            return;
                        }
                        this.setValidatedInfo({
                            file,
                            hash,
                        });
                    }),
                    catchError(({ error }: { error: { data: { error: FileErrorTypes } } }) => {
                        return of(this.fileErrorService.setError(error.data.error));
                    }),
                    finalize(() => this.setValidating(false)),
                )
                .subscribe(),
        );
    }

    private setValidating(validating: boolean) {
        this.subtitlesService.setValidating(validating);
        this._validating$.next(validating);
    }

    private wrongExtension(name: string) {
        return !name.includes(subtitlesConfig.requirements.acceptedFileType);
    }

    private setValidatedInfo(info: FileValidatedInfo) {
        this._validatedInfo$.next(info);
    }
}
